import { PiggyBankIcon, UsersThreeIcon } from '@phosphor-icons/react'
import {
  type PlainDate,
  differenceInDays,
  formatMoney,
  isPlainDate,
  percentOf,
  todayIn,
} from '@paybae/shared'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { ProgressRing } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Interruptor,
  Monto,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import { PersonDot } from '@/components/PersonAvatar'
import {
  ApiError,
  type Account,
  type FundKind,
  type Member,
  type SavingsGoal,
} from '@/lib/api'
import { haptic } from '@/lib/haptics'
import {
  useAccounts,
  useActualizarMeta,
  useAportarMeta,
  useCrearMeta,
  useGoals,
  useHousehold,
} from '@/lib/queries'
import { useSession } from '@/lib/session'

/**
 * Alta, edición y aportación de metas de ahorro.
 *
 * Las tres pantallas viven juntas porque comparten el mismo modelo mental: una
 * meta es un nombre, un monto y —si hay fecha— un ritmo mensual. Separarlas en
 * archivos obligaría a duplicar el cálculo del ritmo, que es justo la cifra que
 * convierte «faltan $133,000» en algo sobre lo que se puede decidir.
 *
 * La decisión que el cliente pidió expresamente es **de quién es la meta**. Se
 * resuelve con un selector explícito y sin valor oculto: `null` es del hogar y
 * un id es de esa persona. No se puede cambiar después, así que se pregunta
 * antes de guardar y se explica en la propia pantalla.
 *
 * La segunda decisión es **qué clase de fondo es** (§7). No todo lo que se
 * ahorra tiene final: una reserva de emergencia no se cumple, se mantiene. Por
 * eso el tipo se elige antes que el monto y cambia lo que el formulario pide —
 * solo una meta obliga a poner objetivo y fecha.
 */

/** Centinelas del selector: `Segmentos` trabaja con cadenas, no con nulos. */
const COMPARTIDA = 'compartida'
const SIN_CUENTA = 'sin-cuenta'

/** Días de un mes promedio; el mismo divisor que usa la pantalla de Ahorro. */
const DIAS_POR_MES = 30.44

/**
 * Tipos de fondo, en el orden en que se eligen de verdad.
 *
 * `GOAL` va primero porque es lo que casi todo el mundo viene a crear, y además
 * es el valor por omisión del servidor: quien no se detiene a elegir acaba con
 * lo mismo que habría elegido.
 */
const TIPOS: ReadonlyArray<OpcionSegmento<FundKind>> = [
  { value: 'GOAL', label: 'Meta', detalle: 'Con monto y fecha' },
  { value: 'EMERGENCY', label: 'Emergencia', detalle: 'Reserva, sin meta' },
  { value: 'GENERAL', label: 'Bolsa', detalle: 'Ahorro sin destino fijo' },
  { value: 'CUSTOM', label: 'Otro', detalle: 'A tu manera' },
]

/** Lo que cada tipo promete, en una línea. Se enseña bajo el selector. */
const EXPLICACION: Record<FundKind, string> = {
  GOAL: 'Tiene final: cuando junte el monto, la meta se marca cumplida.',
  EMERGENCY:
    'No se termina, se mantiene. Aquí no hay porcentaje que completar: lo que importa es el saldo que tengas guardado.',
  GENERAL: 'Una bolsa para lo que aún no tiene destino. Sin meta, sin fecha.',
  CUSTOM: 'Un fondo sin reglas: junta lo que quieras, sin monto que alcanzar.',
}

// ═══════════════════════════════════════════════════════════════════════════
//  Alta y edición de una meta
// ═══════════════════════════════════════════════════════════════════════════

export function GoalForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { membership } = useSession()

  const metas = useGoals()
  const hogar = useHousehold()
  const cuentas = useAccounts()

  const cerrar = (): void => {
    void navigate('/ahorro')
  }

  // Al editar hacen falta las tres consultas; al crear, solo hogar y cuentas.
  // Aun así se espera a todas: dibujar el formulario sin la lista de metas
  // dejaría el modo edición con los campos en blanco y el usuario sobrescribiría
  // su meta con nada.
  if (metas.isPending || hogar.isPending || cuentas.isPending) {
    return <HojaCargando titulo={id ? 'Editar fondo' : 'Nuevo fondo'} onCerrar={cerrar} />
  }

  const fallo = metas.error ?? hogar.error ?? cuentas.error
  if (fallo || !hogar.data || !metas.data || !cuentas.data) {
    return (
      <HojaDeAviso titulo={id ? 'Editar fondo' : 'Nuevo fondo'} onCerrar={cerrar}>
        <ErrorState
          titulo="No pudimos cargar el formulario"
          mensaje={fallo instanceof Error ? fallo.message : 'Inténtalo de nuevo.'}
          onReintentar={() => {
            void metas.refetch()
            void hogar.refetch()
            void cuentas.refetch()
          }}
        />
      </HojaDeAviso>
    )
  }

  const meta = id ? (metas.data.find((g) => g.id === id) ?? null) : null
  if (id && !meta) {
    return (
      <HojaDeAviso titulo="Editar fondo" onCerrar={cerrar}>
        <ErrorState
          titulo="No encontramos este fondo"
          mensaje="Puede que se haya archivado desde otro dispositivo."
        />
      </HojaDeAviso>
    )
  }

  return (
    <FormularioMeta
      meta={meta}
      integrantes={hogar.data.members}
      // El servidor rechaza los vales como cuenta de una meta, y las de ahorro
      // son las únicas que tienen sentido ligar: es donde el traspaso aterriza.
      cuentasAhorro={cuentas.data.filter((c) => !c.archivedAt && c.type === 'SAVINGS')}
      hoy={todayIn(membership.household.timezone)}
      // En un hogar personal no hay a quién atribuir: preguntarlo sería un
      // selector de una sola opción.
      esPersonal={membership.household.kind === 'PERSONAL'}
      miMemberId={membership.id}
      onCerrar={cerrar}
    />
  )
}

function FormularioMeta({
  meta,
  integrantes,
  cuentasAhorro,
  hoy,
  esPersonal,
  miMemberId,
  onCerrar,
}: {
  meta: SavingsGoal | null
  integrantes: Member[]
  cuentasAhorro: Account[]
  hoy: PlainDate
  esPersonal: boolean
  miMemberId: string
  onCerrar: () => void
}) {
  const crear = useCrearMeta()
  const actualizar = useActualizarMeta(meta?.id ?? '')

  const [nombre, setNombre] = useState(meta?.name ?? '')
  const [tipo, setTipo] = useState<FundKind>(meta?.kind ?? 'GOAL')
  const [objetivoCents, setObjetivoCents] = useState(meta?.targetAmountCents ?? 0)
  const [fecha, setFecha] = useState<string>(meta?.targetDate ?? '')
  // Por omisión la meta es de quien la captura: es el valor de menor alcance, y
  // ponerla en el hogar por descuido la haría visible para toda la pareja.
  const [dueno, setDueno] = useState<string>(meta ? (meta.ownerMemberId ?? COMPARTIDA) : miMemberId)
  const [cuenta, setCuenta] = useState<string>(meta?.linkedAccountId ?? SIN_CUENTA)
  const [pausada, setPausada] = useState(meta?.status === 'PAUSED')

  const editando = meta !== null
  const guardando = crear.isPending || actualizar.isPending
  const error = crear.error ?? actualizar.error

  // Solo una meta necesita objetivo. En los demás fondos el monto es de
  // referencia: si se deja en cero, el fondo se guarda sin objetivo y su avance
  // pasa a ser el saldo acumulado.
  const esMeta = tipo === 'GOAL'

  // Lo ya aportado no se vuelve a pedir: el ritmo se calcula sobre lo que falta.
  const yaAhorrado = meta?.currentAmountCents ?? 0
  const faltante = Math.max(0, objetivoCents - yaAhorrado)
  const ritmo = esMeta ? calcularRitmo(faltante, fecha, hoy) : null

  const nombreValido = nombre.trim().length >= 2
  const listo = nombreValido && (!esMeta || objetivoCents > 0)

  const guardar = (): void => {
    if (!listo || guardando) return

    const comun = {
      name: nombre.trim(),
      kind: tipo,
      // `null` explícito, no ausente: es lo que quita un objetivo que ya estaba
      // puesto cuando el fondo deja de ser meta.
      targetAmountCents: objetivoCents > 0 ? objetivoCents : null,
      // Cadena vacía no es «sin fecha» para el servidor: hay que mandar `null`
      // explícito para borrar una fecha que ya estaba puesta. Un fondo sin meta
      // tampoco tiene para cuándo.
      targetDate: esMeta && isPlainDate(fecha) ? fecha : null,
      linkedAccountId: cuenta === SIN_CUENTA ? null : cuenta,
    }

    const alGuardar = {
      onSuccess: () => {
        haptic('success')
        onCerrar()
      },
      // Sin limpiar el formulario: si falló la red, lo capturado sigue ahí y el
      // segundo intento no cuesta ni un toque más.
      onError: () => haptic('error'),
    }

    if (meta) {
      const pausadaAntes = meta.status === 'PAUSED'
      actualizar.mutate(
        {
          ...comun,
          // Solo viaja si de verdad cambió: mandar `ACTIVE` en una meta ya
          // alcanzada le quitaría la marca de alcanzada sin que nadie lo pida.
          status: pausada === pausadaAntes ? undefined : pausada ? 'PAUSED' : 'ACTIVE',
        },
        alGuardar,
      )
      return
    }

    crear.mutate(
      {
        ...comun,
        // En hogar personal no hay selector, así que la meta queda a nombre de
        // quien la crea.
        ownerMemberId: esPersonal ? miMemberId : dueno === COMPARTIDA ? null : dueno,
      },
      alGuardar,
    )
  }

  const opcionesDueno: OpcionSegmento<string>[] = [
    { value: COMPARTIDA, label: 'Compartida', detalle: 'De todo el hogar' },
    ...integrantes.map((m) => ({
      value: m.id,
      label: nombreCorto(m.user.displayName),
      detalle: 'Ahorro personal',
    })),
  ]

  const opcionesCuenta: OpcionSegmento<string>[] = [
    { value: SIN_CUENTA, label: 'Ninguna', detalle: 'Solo la llevo aquí' },
    ...cuentasAhorro.map((c) => ({
      value: c.id,
      label: c.name,
      detalle: formatMoney(c.balanceCents, { hideZeroCents: true }),
    })),
  ]

  const duenoElegido = integrantes.find((m) => m.id === dueno) ?? null

  return (
    <FormSheet
      tinte="mint"
      titulo={editando ? 'Editar fondo' : 'Nuevo fondo'}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError mensaje={error ? mensajeDeError(error) : null} />
          <Guardar onClick={guardar} disabled={!listo} guardando={guardando}>
            {etiquetaGuardar(nombreValido, esMeta, objetivoCents, editando)}
          </Guardar>
        </div>
      }
    >
      <Campo
        label="¿Para qué estás ahorrando?"
        ayuda="Un nombre concreto —«Viaje a Oaxaca»— se reconoce de un vistazo en la lista."
      >
        <Texto
          value={nombre}
          onChange={setNombre}
          placeholder="Fondo de emergencia"
          maxLength={80}
          invalido={nombre.length > 0 && !nombreValido}
        />
      </Campo>

      {/* El tipo va antes que el monto porque decide si el monto es obligatorio.
          Preguntarlo después dejaría al usuario tecleando una cifra que quizá no
          hacía falta. */}
      <Campo label="¿Qué clase de fondo es?" ayuda={EXPLICACION[tipo]}>
        <Segmentos opciones={TIPOS} value={tipo} onChange={setTipo} />
      </Campo>

      <Campo
        label={esMeta ? '¿Cuánto quieres juntar?' : 'Monto de referencia (opcional)'}
        ayuda={
          esMeta
            ? undefined
            : 'Déjalo en cero si no quieres un número que alcanzar: el fondo mostrará el saldo que lleves guardado.'
        }
      >
        <Monto cents={objetivoCents} onChange={setObjetivoCents} />
      </Campo>

      {esMeta ? (
        <Campo
          label="¿Para cuándo? (opcional)"
          ayuda="Con fecha te decimos cuánto apartar al mes; sin ella, la meta no caduca."
          error={ritmo?.tipo === 'vencida' ? 'Esa fecha ya pasó. La meta aparecerá como vencida.' : undefined}
        >
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
          />
        </Campo>
      ) : null}

      {ritmo?.tipo === 'ritmo' ? (
        // En vivo mientras se teclea: es la cifra que decide si la meta es
        // realista, y saberlo después de guardar llega tarde.
        <p className="rounded-card bg-primary-soft px-3.5 py-3 text-[13px] leading-snug text-ink">
          Para llegar en {ritmo.meses} {ritmo.meses === 1 ? 'mes' : 'meses'}, tendrías que apartar{' '}
          <strong data-money className="font-semibold">
            {formatMoney(ritmo.cents, { hideZeroCents: true })}
          </strong>{' '}
          al mes.
          {yaAhorrado > 0 ? (
            <>
              {' '}
              Ya llevas {formatMoney(yaAhorrado, { hideZeroCents: true })}.
            </>
          ) : null}
        </p>
      ) : null}

      {esPersonal ? null : editando ? (
        // El servidor no deja mover una meta de bolsillo: reescribiría a quién se
        // le descontó el ahorro ya aportado. Se muestra, pero no se ofrece.
        <Campo
          label="¿De quién es este fondo?"
          ayuda="El dueño no se puede cambiar: movería el ahorro ya aportado de bolsillo. Si te equivocaste, crea otro fondo."
        >
          <p className="flex min-h-[48px] items-center gap-2 rounded-card border border-line bg-surface-sunken px-3.5 text-[14px] text-ink">
            {duenoElegido ? (
              <>
                {/* El punto va con `personColorToken`, nunca con el id del
                    integrante: ambos son cadenas y pasar el id dejaría a todo el
                    hogar del mismo color sin que el compilador se queje. */}
                <PersonDot token={duenoElegido.personColorToken} />
                Ahorro personal de {nombreCorto(duenoElegido.user.displayName)}
              </>
            ) : (
              <>
                <UsersThreeIcon size={17} weight="fill" className="text-ink-soft" />
                Meta compartida del hogar
              </>
            )}
          </p>
        </Campo>
      ) : (
        <Campo label="¿De quién es este fondo?">
          <Segmentos
            opciones={opcionesDueno}
            value={dueno}
            onChange={setDueno}
            columnas={opcionesDueno.length === 3 ? 3 : 2}
          />
          <p className="mt-1.5 flex items-start gap-2 text-[12px] leading-snug text-ink-soft">
            {duenoElegido ? (
              <>
                <PersonDot
                  token={duenoElegido.personColorToken}
                  className="mt-1.5"
                />
                Se descuenta del dinero libre de {nombreCorto(duenoElegido.user.displayName)} y
                aparece en su bolsillo, no en el del hogar.
              </>
            ) : (
              <>
                <UsersThreeIcon size={14} weight="fill" className="mt-0.5 shrink-0" />
                Sale del fondo común: cuenta para todo el hogar y cualquiera puede aportar.
              </>
            )}
          </p>
        </Campo>
      )}

      {cuentasAhorro.length > 0 ? (
        <Campo
          label="Cuenta de ahorro ligada (opcional)"
          ayuda="Ligar una cuenta permite registrar el traspaso de verdad al aportar; sin ella el ahorro solo queda comprometido."
        >
          <Segmentos opciones={opcionesCuenta} value={cuenta} onChange={setCuenta} />
        </Campo>
      ) : (
        <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
          Todavía no tienes cuentas de ahorro. Crea una desde Ajustes y podrás ligarla a esta
          meta para registrar los traspasos de verdad.
        </p>
      )}

      {editando && meta.status !== 'ACHIEVED' ? (
        <Interruptor
          label="Pausar este fondo"
          ayuda="Deja de contar para el ahorro comprometido del mes. Lo aportado no se pierde."
          value={pausada}
          onChange={setPausada}
        />
      ) : null}
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Aportar a una meta
// ═══════════════════════════════════════════════════════════════════════════

export function GoalContribution() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { membership } = useSession()

  const metas = useGoals()
  const cuentas = useAccounts()

  const cerrar = (): void => {
    void navigate('/ahorro')
  }

  if (metas.isPending || cuentas.isPending) {
    return <HojaCargando titulo="Aportar" onCerrar={cerrar} />
  }

  const fallo = metas.error ?? cuentas.error
  if (fallo || !metas.data || !cuentas.data) {
    return (
      <HojaDeAviso titulo="Aportar" onCerrar={cerrar}>
        <ErrorState
          titulo="No pudimos cargar la meta"
          mensaje={fallo instanceof Error ? fallo.message : 'Inténtalo de nuevo.'}
          onReintentar={() => {
            void metas.refetch()
            void cuentas.refetch()
          }}
        />
      </HojaDeAviso>
    )
  }

  const meta = metas.data.find((g) => g.id === id) ?? null
  if (!meta) {
    return (
      <HojaDeAviso titulo="Aportar" onCerrar={cerrar}>
        <ErrorState
          titulo="No encontramos este fondo"
          mensaje="Puede que se haya archivado desde otro dispositivo."
        />
      </HojaDeAviso>
    )
  }

  return (
    <FormularioAportacion
      meta={meta}
      // Los vales solo compran despensa: el servidor los rechaza como origen de
      // un ahorro. La cuenta ligada tampoco puede ser el origen —sería un
      // traspaso a sí misma.
      cuentasOrigen={cuentas.data.filter(
        (c) => !c.archivedAt && c.type !== 'MEAL_VOUCHER' && c.id !== meta.linkedAccountId,
      )}
      hoy={todayIn(membership.household.timezone)}
      onCerrar={cerrar}
    />
  )
}

function FormularioAportacion({
  meta,
  cuentasOrigen,
  hoy,
  onCerrar,
}: {
  meta: SavingsGoal
  cuentasOrigen: Account[]
  hoy: PlainDate
  onCerrar: () => void
}) {
  const aportar = useAportarMeta(meta.id)

  const [montoCents, setMontoCents] = useState(0)
  const [fecha, setFecha] = useState<string>(hoy)
  const [transferido, setTransferido] = useState(false)
  const [origen, setOrigen] = useState<string>(cuentasOrigen[0]?.id ?? '')
  const [nota, setNota] = useState('')

  // El traspaso real necesita una cuenta donde aterrice el dinero. Sin cuenta
  // ligada el servidor lo rechaza, así que ni se ofrece el interruptor.
  const puedeTransferir = meta.linkedAccountId !== null && cuentasOrigen.length > 0

  const fechaValida = isPlainDate(fecha)
  const listo = montoCents > 0 && fechaValida && (!transferido || origen !== '')

  const proyectado = meta.currentAmountCents + montoCents
  // Un fondo sin objetivo no tiene porcentaje que proyectar: lo que crece es el
  // saldo. Se guarda el objetivo en una constante para que el `null` se
  // compruebe una sola vez y el JSX no tenga que repetirlo.
  const objetivo = meta.targetAmountCents
  const pctProyectado = objetivo === null ? null : percentOf(proyectado, objetivo)

  const guardar = (): void => {
    if (!listo || aportar.isPending) return

    aportar.mutate(
      {
        amountCents: montoCents,
        occurredOn: fecha as PlainDate,
        note: nota.trim() || null,
        transferred: transferido,
        fromAccountId: transferido ? origen : null,
      },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        onError: () => haptic('error'),
      },
    )
  }

  const opcionesOrigen: OpcionSegmento<string>[] = cuentasOrigen.map((c) => ({
    value: c.id,
    label: c.name,
    detalle: formatMoney(c.balanceCents, { hideZeroCents: true }),
  }))

  return (
    <FormSheet
      tinte="mint"
      titulo={`Aportar a ${meta.name}`}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError mensaje={aportar.error ? mensajeDeError(aportar.error) : null} />
          <Guardar onClick={guardar} disabled={!listo} guardando={aportar.isPending}>
            {montoCents <= 0
              ? 'Escribe el monto'
              : transferido && origen === ''
                ? 'Elige de dónde sale'
                : 'Registrar aportación'}
          </Guardar>
        </div>
      }
    >
      {/* Anillo, no barra: es la misma forma con la que se lee el avance en el
          resto de la app, y aquí muestra dónde quedaría la meta con lo que se
          está tecleando — antes de guardar, que es cuando sirve.

          Sin objetivo no hay anillo que llenar, así que el icono va en un disco
          liso: un anillo al 0% se leería como «no llevas nada», y una reserva de
          emergencia con saldo lleva bastante. */}
      <div className="flex items-center gap-4 rounded-card border border-line bg-surface p-3.5">
        {pctProyectado === null ? (
          <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-surface-sunken">
            <PiggyBankIcon size={24} weight="fill" className="text-ink-soft" />
          </span>
        ) : (
          <ProgressRing percent={pctProyectado} size={64} stroke={7}>
            <span data-money className="text-[13px] font-bold text-ink">
              {Math.round(pctProyectado)}%
            </span>
          </ProgressRing>
        )}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-[14px] font-semibold text-ink">
            <PiggyBankIcon size={16} weight="fill" className="shrink-0 text-ink-soft" />
            {meta.name}
          </p>
          <p data-money className="text-[12px] text-ink-soft">
            {formatMoney(meta.currentAmountCents, { hideZeroCents: true })}
            {objetivo === null
              ? ' guardados'
              : ` de ${formatMoney(objetivo, { hideZeroCents: true })}`}
            {montoCents > 0 ? (
              <>
                {' '}
                → <strong className="text-ink">{formatMoney(proyectado, { hideZeroCents: true })}</strong>
              </>
            ) : null}
          </p>
        </div>
      </div>

      <Campo label="¿Cuánto vas a apartar?">
        <Monto cents={montoCents} onChange={setMontoCents} />
      </Campo>

      <Campo
        label="¿Qué día?"
        error={fechaValida ? undefined : 'Escribe la fecha como AAAA-MM-DD.'}
      >
        <input
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
        />
      </Campo>

      {puedeTransferir ? (
        <Interruptor
          label="Ya transferí el dinero"
          ayuda="Actívalo solo si el dinero ya salió de tu cuenta y está en la de ahorro."
          value={transferido}
          onChange={setTransferido}
        />
      ) : null}

      {transferido && puedeTransferir ? (
        <>
          <Campo label="¿De qué cuenta salió?">
            <Segmentos opciones={opcionesOrigen} value={origen} onChange={setOrigen} />
          </Campo>
          <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
            Movemos el saldo de verdad: sale de la cuenta que elijas, entra en la cuenta ligada a
            la meta y queda registrado como movimiento.
          </p>
        </>
      ) : (
        // La explicación que pidió el cliente: comprometido no es transferido.
        <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
          Mientras no lo transfieras, el monto queda <strong className="text-ink">comprometido</strong>:
          se descuenta de tu disponible para que no lo vuelvas a gastar, pero el dinero sigue en tu
          cuenta de origen y ningún saldo se mueve.
          {meta.linkedAccountId === null
            ? ' Para registrar el traspaso, liga una cuenta de ahorro a la meta desde Editar.'
            : ''}
        </p>
      )}

      <Campo label="Nota (opcional)" ayuda="De dónde salió, para acordarte dentro de tres meses.">
        <Texto
          value={nota}
          onChange={setNota}
          placeholder="Del aguinaldo"
          maxLength={280}
        />
      </Campo>

      {meta.progress.pendingTransferCents > 0 ? (
        <p className="rounded-card bg-surface-sunken px-3.5 py-3 text-[12px] leading-snug text-ink-soft">
          De lo que llevas aportado,{' '}
          <strong data-money className="text-ink">
            {formatMoney(meta.progress.pendingTransferCents, { hideZeroCents: true })}
          </strong>{' '}
          todavía no se traspasa: está comprometido, pero sigue en tu cuenta.
        </p>
      ) : null}
    </FormSheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Piezas compartidas
// ═══════════════════════════════════════════════════════════════════════════

type Ritmo =
  | { tipo: 'ritmo'; meses: number; cents: number }
  | { tipo: 'vencida' }
  | null

/**
 * Cuánto habría que apartar al mes para llegar a la fecha.
 *
 * Se redondea al peso **hacia arriba** por dos razones: apartar de menos deja
 * la meta corta justo el último mes, cuando ya no hay margen; y «aparta
 * $12,583.34 al mes» es una instrucción que nadie puede seguir — los centavos
 * convierten una meta en un cálculo.
 */
function calcularRitmo(faltanteCents: number, fecha: string, hoy: PlainDate): Ritmo {
  if (!isPlainDate(fecha)) return null

  const dias = differenceInDays(fecha, hoy)
  if (dias < 0) return { tipo: 'vencida' }
  if (faltanteCents <= 0) return null

  const meses = Math.max(1, Math.ceil(dias / DIAS_POR_MES))
  return { tipo: 'ritmo', meses, cents: Math.ceil(faltanteCents / meses / 100) * 100 }
}

/**
 * Un «Revisa los datos del formulario» sin más no dice qué corregir; el detalle
 * por campo que manda el servidor sí.
 */
function mensajeDeError(error: unknown): string {
  if (error instanceof ApiError) {
    const detalles = Object.values(error.fieldErrors ?? {}).flat()
    return detalles.length > 0 ? detalles.join('. ') : error.message
  }
  return 'No pudimos guardar. Inténtalo de nuevo.'
}

/** Dice qué falta, en vez de dejar un botón muerto sin explicación. */
function etiquetaGuardar(
  nombreValido: boolean,
  esMeta: boolean,
  objetivoCents: number,
  editando: boolean,
): string {
  if (!nombreValido) return 'Ponle nombre al fondo'
  // El monto solo bloquea a las metas: un fondo sin objetivo se guarda con cero.
  if (esMeta && objetivoCents <= 0) return 'Escribe el monto objetivo'
  if (editando) return 'Guardar cambios'
  return esMeta ? 'Crear meta' : 'Crear fondo'
}

function nombreCorto(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName
}

/** Hoja con el mismo esqueleto que el formulario, para que nada salte al cargar. */
function HojaCargando({ titulo, onCerrar }: { titulo: string; onCerrar: () => void }) {
  return (
    <FormSheet
      tinte="mint"
      titulo={titulo}
      onCerrar={onCerrar}
      accion={<Guardar onClick={() => undefined} disabled>Cargando…</Guardar>}
    >
      <Skeleton className="h-[72px]" />
      <Skeleton className="h-[180px]" />
      <Skeleton className="h-[72px]" />
      <Skeleton className="h-[104px]" />
    </FormSheet>
  )
}

function HojaDeAviso({
  titulo,
  onCerrar,
  children,
}: {
  titulo: string
  onCerrar: () => void
  children: React.ReactNode
}) {
  return (
    <FormSheet
      tinte="mint"
      titulo={titulo}
      onCerrar={onCerrar}
      accion={<Guardar onClick={onCerrar}>Volver a Ahorro</Guardar>}
    >
      {children}
    </FormSheet>
  )
}
